import React, { useState, useEffect } from 'react';
import { Trash2, CheckCircle, Mail, Eye, Loader2, MailOpen } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

import { PHP_API } from '../config/api';

const statusColors = {
  unread: 'bg-amber-100 text-amber-800',
  read: 'bg-blue-100 text-blue-800',
  replied: 'bg-green-100 text-green-800',
};

const statusIcons = {
  unread: Mail,
  read: MailOpen,
  replied: CheckCircle,
};

export default function AdminContacts() {
  const { getAuthHeaders } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(PHP_API.contacts, getAuthHeaders());
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contact, newStatus) => {
    try {
      await axios.put(`${PHP_API.contactStatus}?id=${contact.id}&status=${newStatus}`, {}, getAuthHeaders());
      toast.success('Status updated');
      fetchContacts();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleView = (contact) => {
    setSelectedContact(contact);
    setIsViewOpen(true);
    // Mark as read if unread
    if (contact.status === 'unread') {
      handleStatusChange(contact, 'read');
    }
  };

  const handleDelete = (contact) => {
    setSelectedContact(contact);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${PHP_API.contact}?id=${selectedContact.id}`, getAuthHeaders());
      toast.success('Contact deleted');
      setIsDeleteOpen(false);
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div data-testid="admin-contacts">
      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl text-stone-900">Contact Messages</h1>
        <p className="text-stone-600 mt-1">View and manage contact form submissions</p>
      </div>

      {/* Contacts Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-600">No contact messages yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Contact</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Subject</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Status</th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-stone-700">Date</th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {contacts.map((contact) => {
                  const StatusIcon = statusIcons[contact.status];
                  return (
                    <tr key={contact.id} className={`table-row-hover ${contact.status === 'unread' ? 'bg-stone-50' : ''}`}>
                      <td className="px-6 py-4">
                        <p className={`font-medium text-stone-900 ${contact.status === 'unread' ? 'font-bold' : ''}`}>
                          {contact.name}
                        </p>
                        <p className="text-stone-500 text-sm">{contact.email}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className={`text-stone-700 ${contact.status === 'unread' ? 'font-semibold' : ''}`}>
                          {contact.subject}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <Select
                          value={contact.status}
                          onValueChange={(value) => handleStatusChange(contact, value)}
                        >
                          <SelectTrigger className="w-[120px]">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusColors[contact.status]}`}>
                              <StatusIcon className="w-3 h-3" />
                              {contact.status}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="unread">Unread</SelectItem>
                            <SelectItem value="read">Read</SelectItem>
                            <SelectItem value="replied">Replied</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-6 py-4 text-stone-500 text-sm">{formatDate(contact.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(contact)}
                            className="hover:bg-primary/10 hover:text-primary"
                            data-testid={`view-contact-${contact.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(contact)}
                            className="hover:bg-red-50 hover:text-red-600"
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Contact Message</DialogTitle>
          </DialogHeader>

          {selectedContact && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-stone-500 text-sm">Name</p>
                  <p className="font-medium text-stone-900">{selectedContact.name}</p>
                </div>
                <div>
                  <p className="text-stone-500 text-sm">Email</p>
                  <p className="font-medium text-stone-900">{selectedContact.email}</p>
                </div>
                {selectedContact.phone && (
                  <div>
                    <p className="text-stone-500 text-sm">Phone</p>
                    <p className="font-medium text-stone-900">{selectedContact.phone}</p>
                  </div>
                )}
                <div className={selectedContact.phone ? '' : 'col-span-2'}>
                  <p className="text-stone-500 text-sm">Subject</p>
                  <p className="font-medium text-stone-900">{selectedContact.subject}</p>
                </div>
              </div>
              
              <div>
                <p className="text-stone-500 text-sm mb-2">Message</p>
                <div className="bg-stone-50 rounded-lg p-4">
                  <p className="text-stone-700 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-stone-100">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${statusColors[selectedContact.status]}`}>
                  {selectedContact.status}
                </span>
                <span className="text-stone-500 text-sm">{formatDate(selectedContact.created_at)}</span>
              </div>

              {/* Reply Button */}
              <div className="pt-2">
                <a
                  href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                  className="btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
                  onClick={() => handleStatusChange(selectedContact, 'replied')}
                >
                  <Mail className="w-4 h-4" />
                  Reply via Email
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message from {selectedContact?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


